// ============================================================================
// Service: SafetyManager365 Document Distribution, Read & Sign, CBT & Knowledge Base
// ============================================================================

export interface DistributedDocument {
  id: number;
  categoryId?: string | null;
  documentCode: string;
  title: string;
  description?: string | null;
  currentRevisionNumber: string;
  filePath: string;
  fileSizeBytes: number;
  mimeType: string;
  checksumSha256: string;
  status: 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED' | 'ARCHIVED';
  requiresAcknowledgment: boolean;
  acknowledgmentDeadlineDays: number;
  publishedAt?: string | null;
  publishedBy?: string | null;
}

export interface PendingDocumentItem {
  documentId: number;
  documentCode: string;
  title: string;
  currentRevisionNumber: string;
  filePath: string;
  publishedAt?: string | null;
  deadlineDate?: string | null;
  daysRemaining?: number;
  isOverdue: boolean;
  firstViewedAt?: string | null;
}

export interface DocumentComplianceReport {
  documentId: number;
  documentCode: string;
  title: string;
  currentRevisionNumber: string;
  totalAssigned: number;
  acknowledgedCount: number;
  pendingCount: number;
  overdueCount: number;
  complianceRate: number;
  users: Array<{
    userId: string;
    userName?: string;
    department?: string;
    firstViewedAt?: string | null;
    acknowledgedAt?: string | null;
    isAcknowledged: boolean;
    isOverdue: boolean;
    ipAddress?: string | null;
    userAgent?: string | null;
  }>;
}

export interface CbtCourse {
  id: number;
  code: string;
  title: string;
  description?: string | null;
  category: string;
  isMandatory: boolean;
  validityMonths: number;
  isActive: boolean;
  totalDurationSeconds?: number;
  userProgressPercentage?: number;
  userCourseStatus?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  modules?: CbtModule[];
}

export interface CbtModule {
  id: number;
  courseId: number;
  sequenceOrder: number;
  title: string;
  videoUrlOrPath: string;
  durationSeconds: number;
  minRequiredWatchPercent: number;
  userProgress?: {
    status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
    watchedSeconds: number;
    highestTimestampReached: number;
    completionPercentage: number;
    completedAt?: string | null;
  } | null;
}

export interface KnowledgeBaseArticle {
  id: number;
  category: string;
  title: string;
  contentMarkdown?: string | null;
  attachedPdfPath?: string | null;
  sortOrder: number;
  isActive: boolean;
}

export const docDistributionService = {
  // 1. Pending Documents for Current User
  async getMyPendingDocuments(userId?: string): Promise<PendingDocumentItem[]> {
    const url = userId ? `/api/v1/documents/my-pending?user_id=${userId}` : '/api/v1/documents/my-pending';
    const res = await fetch(url);
    return res.json();
  },

  // 2. Mark First Viewed
  async markViewed(documentId: number, revisionNumber?: string, userId?: string): Promise<any> {
    const res = await fetch(`/api/v1/documents/${documentId}/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ revision_number: revisionNumber, user_id: userId }),
    });
    return res.json();
  },

  // 3. Acknowledge (Read & Sign)
  async acknowledgeDocument(
    documentId: number,
    revisionNumber?: string,
    userId?: string
  ): Promise<{ status: string; timestamp: string }> {
    const res = await fetch(`/api/v1/documents/${documentId}/acknowledge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ revision_number: revisionNumber, user_id: userId }),
    });
    return res.json();
  },

  // 4. Compliance Report
  async getComplianceReport(documentId: number): Promise<DocumentComplianceReport> {
    const res = await fetch(`/api/v1/documents/${documentId}/compliance-report`);
    return res.json();
  },

  // 5. CBT Courses
  async getCbtCourses(userId?: string): Promise<CbtCourse[]> {
    const url = userId ? `/api/v1/cbt/courses?user_id=${userId}` : '/api/v1/cbt/courses';
    const res = await fetch(url);
    return res.json();
  },

  // 6. CBT Course Details
  async getCbtCourseDetails(courseId: number, userId?: string): Promise<CbtCourse> {
    const url = userId ? `/api/v1/cbt/courses/${courseId}?user_id=${userId}` : `/api/v1/cbt/courses/${courseId}`;
    const res = await fetch(url);
    return res.json();
  },

  // 7. CBT Heartbeat
  async sendCbtHeartbeat(
    moduleId: number,
    currentPositionSeconds: number,
    userId?: string
  ): Promise<{ progress: any; suspicious_seek_detected: boolean }> {
    const res = await fetch(`/api/v1/cbt/modules/${moduleId}/heartbeat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ current_position_seconds: currentPositionSeconds, user_id: userId }),
    });
    return res.json();
  },

  // 8. Knowledge Base Articles
  async getKnowledgeBaseArticles(filter?: { category?: string; search?: string }): Promise<KnowledgeBaseArticle[]> {
    const params = new URLSearchParams();
    if (filter?.category) params.append('category', filter.category);
    if (filter?.search) params.append('search', filter.search);
    const res = await fetch(`/api/v1/knowledge-base/articles?${params.toString()}`);
    return res.json();
  },
};
